import React, { useMemo } from "react";
import cx from "classnames";
import { Typography, Paper, Icon } from "@mui/material";
import moment from "moment";
import { useIntl } from "react-intl";

import { Avatar } from "Components";

const MessageBubble = ({ sender, message, chatUsers, chatType = "team" }) => {
  const { locale } = useIntl();
  
  // Memoize timestamp calculation
  const timestampLabel = useMemo(() => {
    const localDate = moment(message?.createdOn).local();
    return moment(localDate).locale(locale).format("h:mm a").replace("pm", "p.m").replace("am", "a.m");
  }, [message?.createdOn, locale]);

  const isIncoming = sender === "bot" || sender === "ai";
  const typeClass = `chat-type-${chatType}`;

  // Memoize user lookup
  const userChat = useMemo(() => 
    chatUsers?.find((user) => user.authId === message?.transmitterEntityNameId),
    [chatUsers, message?.transmitterEntityNameId]
  );

  return (
    <div className={cx("chat-message", typeClass, {
      "chat-message--incoming": isIncoming,
      "chat-message--outgoing": !isIncoming,
    })}
    >
      {isIncoming && (
        <div className="chat-message-avatar">
          <Avatar
            name={userChat?.label}
            fileKey={userChat?.avatarFile?.fileKey}
            width="40px"
          />
        </div>
      )}

      <div className="chat-message-content">
        <Paper variant="outlined" className="chat-message-body">
          <Typography variant="body1" component="p">
            {message?.message}
          </Typography>
        </Paper>

        <div className="chat-message-meta">
          <Typography variant="body2" component="span">
            {timestampLabel}
          </Typography>

          {!isIncoming && message?.status === "Sent" && (
            <Icon className="message-status-indicator">check</Icon>
          )}

          {!isIncoming && message?.status !== "Sent" && (
            <Icon className={cx("message-status-indicator", message?.status === "Read" && "message-status-indicator--read")}>done_all</Icon>
          )}
        </div>
      </div>
    </div>
  );
};

// Memoize component to prevent unnecessary re-renders
export default React.memo(MessageBubble, (prevProps, nextProps) => {
  return (
    prevProps.sender === nextProps.sender &&
    prevProps.message?.id === nextProps.message?.id &&
    prevProps.message?.status === nextProps.message?.status &&
    prevProps.message?.message === nextProps.message?.message &&
    prevProps.message?.createdOn === nextProps.message?.createdOn &&
    prevProps.chatType === nextProps.chatType &&
    prevProps.chatUsers === nextProps.chatUsers
  );
});
